import { IonContent, IonPage, IonCol, IonRow, IonText, IonGrid, IonButton, useIonViewWillEnter, IonIcon, IonItem, IonLabel, IonCheckbox, IonSelect, IonSelectOption, IonFooter } from '@ionic/react';
import React, { useContext, useRef, useState } from 'react';
import PageHeader from '../../components/PageHeader/PageHeader';
import './SyncUserData.scss';
import IRouteInfo from '../../interfaces/IRouteInfo';
import ServerDBContext from '../../components/ServerDBContext/ServerDBContext';
import PrayerService from '../../services/PrayerService';
import ReadingPlanRecordService from '../../services/ReadingPlanRecordService';
import PrayerStatus from '../../data/PrayerStatus';
import { cloudDownloadOutline, cloudUploadOutline, person, sync } from 'ionicons/icons';
import { useDispatch, useSelector } from 'react-redux';
import { SelectLoginStatus } from '../../store/UserSlice';
import { AppRouteInfo, AppRouteKeys } from '../../routes/AppRoutes';
import PlanSyncOptions, { PlanSyncOptionsValues } from '../../data/PlanSyncOptions';
import DocumentDataMigrationService from '../../services/DocumentDataMigrationService';
import NetworkNotification from '../../components/NetworkNotification/NetworkNotification';
import { SelectGroupIds } from '../../store/FellowshipSlice';
import { UpdateActivePlanByIdAsync } from '../../store/BibleStudySlice';
import { ReadingPlanCodes } from '../../data/ReadingPlans';

const SyncUserData: React.FC<IRouteInfo> = (props: IRouteInfo) => {
  const dispatch = useDispatch();
  const dbService = useContext(ServerDBContext);
  const LoggedIn = useSelector(SelectLoginStatus);
  const userGroupsIds = useSelector(SelectGroupIds);
  const contentRef = useRef<HTMLIonContentElement | null>(null);
  const [syncReport, setSyncReport] = useState<JSX.Element[]>([]);
  const [showRequirement, setShowRequirement] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState(false);
  const [readingPlanOptionSelected, setReadingPlanOptionSelected] = useState<PlanSyncOptionsValues | ''>('');

  useIonViewWillEnter(() => {
    setSyncReport([]);
  })

  const syncUserData = async () => {
    if(privacyPolicy && readingPlanOptionSelected) {
      setShowRequirement(false);
      setSyncReport([]);
      const prayersResponse = await PrayerService.getAllPrayers();
      const readingPlansResponse = await ReadingPlanRecordService.getReadingPlanRecords();
      // Only get users active plans
      const readingPlans = readingPlansResponse.Data ? readingPlansResponse.Data.filter(plan => plan.readingPlanCode !== ReadingPlanCodes.bible &&  (!plan.groupId || userGroupsIds.find(id => plan.groupId === id))) : [];
      const prayers = prayersResponse.Data ? prayersResponse.Data : [];
      console.log('readingPlans: ', readingPlans);
      console.log('prayers: ', prayers);
      const syncResponse = await dbService?.syncUserData(readingPlans, prayers, readingPlanOptionSelected);
      console.log('syncResponse', syncResponse);
      if (syncResponse?.Success) {

        // add new prayers
        const addedPrayerPromises = syncResponse.Data?.prayersToAdd.map(async prayer => {
          const response = await PrayerService.createPrayer(prayer);
          const message = response.Success ? 'ADDED SUCCESSFULLY:' : 'ADDED FAILED:';
          // If there is no title then it is a deleted prayer... don't show it by returning <span />
          return !!prayer.title ? <span className='sync-report-prayer'><b>{message}</b> {prayer.title}</span> : <span />;
        });

        // update existing prayers
        const updatedPrayerPromises = syncResponse.Data?.prayersToUpdate.map(async remotePrayer => {
          let message = '';
          const isNewPrayer = prayers.find(prayer => !!prayer.syncId === false && remotePrayer.title === prayer.title && remotePrayer.id === prayer.id);
          const existingPrayer = prayers.find(prayer => prayer.syncId === remotePrayer.syncId);
          // if isNewPrayer, then it was just synced and has the proper id, if existingPrayer fix the id before updating
          if(!!isNewPrayer) {
            const response = await PrayerService.updatePrayer(remotePrayer, true)
            message = response.Success ? 'PRAYER UPLOADED:' : 'PRAYER SYNC FAILED:';
          } else if(existingPrayer) {
            const response = await PrayerService.updatePrayer({...remotePrayer, id: existingPrayer.id}, true);
            message = response.Success ? remotePrayer.status === PrayerStatus.deleted ? 'PRAYER DELETED:' : 'PRAYER UPDATED:' : 'PRAYER UPDATE FAILED:';
            const title = remotePrayer.status === PrayerStatus.deleted ? existingPrayer.title : remotePrayer.title;
            return <span className='sync-report-prayer'><b>{message}</b> {title}</span>;
          }
          // If there is no title then it is a deleted prayer... don't show it by returning <span />
          return !!remotePrayer.title ? <span className='sync-report-prayer'><b>{message}</b> {remotePrayer.title}</span> : <span />;
        });

        // We couldn't use a foreach loop because it's not async compatible, instead we make a collection of promises to wait for.
        const addedPrayers = syncResponse.Data?.prayersToAdd.length ? await Promise.all(addedPrayerPromises!) : [];
        const updatedPrayers = syncResponse.Data?.prayersToUpdate.length ? await Promise.all(updatedPrayerPromises!) : [];
        //let report: JSX.Element[] | string[] = [...addedPrayers, ...updatedPrayers];
        let report: JSX.Element[] = [...addedPrayers, ...updatedPrayers];

        if(prayers.length !== 0) {
          const prayerReport = <span className='sync-report-prayer'><b>PRAYERS: </b> SYNCED AND ALL UP-TO-DATE</span>
          report.push(prayerReport);
        }

        // add new Reading Plan Records
        const addedPlanRecordPromises = syncResponse.Data?.readingPlansToAdd.map(async remotePlanRecord => {
          console.log('reading plan sync - add:', remotePlanRecord);
          const response = await ReadingPlanRecordService.createReadingPlanRecord(remotePlanRecord);
          const message = response.Success ? 'ADDED SUCCESSFULLY:' : 'ADDED FAILED:';
          return !!remotePlanRecord.name ? 
            <span className='sync-report-plan'><b>{message}</b> {remotePlanRecord.name}</span> : 
            <span className='sync-report-plan'><b>{message}</b> {remotePlanRecord.readingPlan}</span>;
        });

        // update Reading Plan Records
        // Migrate all reading plan records from the cloud to the latest document version
        const updatedPlanRecordPromises = syncResponse.Data?.readingPlansToUpdate.map(async remotePlanRecord => 
          {
            let message = '';
            const isNewPlanRecord = readingPlans.find(planRecord => !!planRecord.syncId === false && remotePlanRecord.name === planRecord.name && remotePlanRecord.id === planRecord.id);
            const existingPlanRecord = readingPlans.find(planRecord => planRecord.syncId === remotePlanRecord.syncId);
            // if isNewPlanRecord, then it was just synced and has the proper id, if existingPlanRecord fix the id before updating
            // NOTE: Group Plans may be marked as isNewPlanRecord, but the id, sessionsPerDay, and startDate are already updated properly.
            if(!!isNewPlanRecord) {
              const response = await ReadingPlanRecordService.updateReadingPlanRecord(remotePlanRecord, true)
              message = response.Success ? '' : 'BIBLE PLAN SYNC FAILED:';
            } else if(existingPlanRecord) {
              const response = await ReadingPlanRecordService.updateReadingPlanRecord({...remotePlanRecord, id: existingPlanRecord.id}, true);
              message =  response.Success ? '' : 'BIBLE PLAN SYNC FAILED:';
            }

            // If no message successfully updated
            console.log('reading plan sync - update:', message);
            if(message!) {
              switch (readingPlanOptionSelected) {
                case PlanSyncOptions.backup:
                  message = 'READING PLAN BACKED UP:';
                  break;
                case PlanSyncOptions.restore:
                  message = 'READING PLAN RESTORED:';
                  break;
              }
            }
            return <span className='sync-report-plan'><b>{message}</b> {remotePlanRecord.name ? remotePlanRecord.name : remotePlanRecord.readingPlan}</span>;
          });

        // We couldn't use a foreach loop because it's not async compatible, instead we make a collection of promises to wait for.
        const addedPlanRecord = syncResponse.Data?.readingPlansToAdd.length ? await Promise.all(addedPrayerPromises!) : [];
        const updatedPlanRecord = syncResponse.Data?.readingPlansToUpdate.length ? await Promise.all(updatedPlanRecordPromises!) : [];
        report = [...report, ...addedPlanRecord, ...updatedPlanRecord];
        // Reload plans into Redux
        UpdateActivePlanByIdAsync(dispatch, userGroupsIds);

        if(readingPlanOptionSelected === PlanSyncOptions.nothing) {
          report.push(<span className='sync-report-prayer'><b>READING PLANS: </b> NOTHING WAS UPDATED</span>);
        }

        setSyncReport(report);
        contentRef.current?.scrollToBottom();
      }
    } else {
      setShowRequirement(true);
    }
  }

  return (
    <IonPage>
      <PageHeader {...props} showBackButton={true} ></PageHeader>
      <IonContent ref={contentRef}>
        <IonGrid fixed={true}>
          <IonRow >
            <IonCol className="announcement-text">
              <h2 className="sync-page-title">PRAYERS AND READING PLAN</h2>
              <p className="sync-page-subtitle">
                <IonIcon icon={cloudUploadOutline} /> backup 
                <IonIcon icon={sync} className="sync-subtitle-icon" /> sync
                <IonIcon icon={cloudDownloadOutline} className="sync-subtitle-icon" /> restore</p>
              <div className="sync-page-description">
                <p>Here you can backup, synchronize, and restore your prayers and reading plan on this device or transfer them to another device.</p> 
                <p>To get started you must either create or join a group and <b>sign into your account</b>. </p>
              </div>
              <h2 className="sync-acknowledgements-title">Usage Acknowledgements...</h2>
              <ul className="sync-page-acknowledgements">
                <li>Your prayers and reading plan <b>will NOT be shared</b> with your group when you backup and sync.</li>
                <li>Your <b>scheduled reading plan day</b> and <b>checked read days</b> can be backed up and restored using this tool.</li>
                <li>If you restore your reading plan from the cloud all your checked days from the <b>cloud will overwrite</b> anything you have checked off on this device previously.</li>
                <li>Old synchronized prayers on this device that have been removed from another synchronized device <b>will be deleted from this device</b> when preforming the sync process.</li>
                <li>All of your prayers on this device will be stored on our server including your "Active", "Answered", and "Revisit" prayers.</li>
                <li>This backup service is <b>only intended for temporary</b> backup and synchronizing across devices. Do not rely on this backup to remain available to you indefinitely.</li>
                <li>If you are no longer in a particular group, this backed up group reading plan cannot be restored.</li>
                <li>All of your backed up and synchronized <b>data will be deleted from our server</b> when you decide to delete your account. However, your existing prayers and non-group reading plans will remain on all your devices when you no longer have an account.</li>
              </ul>
            </IonCol>
          </IonRow>
          { LoggedIn ?
            <IonRow>
              <IonCol className="announcement-text">
                <IonCheckbox className="policy-terms-check-box" color="tertiary" checked={privacyPolicy} onIonChange={e => setPrivacyPolicy(e.detail.checked)} ></IonCheckbox>
                <IonText color="tertiary">I agree to this app's <a rel="noreferrer" target="_blank" href="https://biblestudytogether.com/privacy-policy-create-disciples-and-bible-study-together/">Privacy Policy</a> and&nbsp;
                <a rel="noreferrer" target="_blank" href="https://biblestudytogether.com/app-terms-of-use/">Terms of Use</a>.</IonText>
                <IonText color="danger"
                   className={!showRequirement || privacyPolicy ? 'hidden' : ''}><br />** You must agree to the Privacy Policy and Terms to continue. **</IonText>
                <IonItem lines='full' className="sync-plan-option">
                  <IonSelect label="BIBLE READING PLAN:" labelPlacement="stacked" placeholder="SELECT SYNC OPTION" value={readingPlanOptionSelected}  
                    onIonChange={e => setReadingPlanOptionSelected(e.detail.value?.toString())} interface='action-sheet' color="tertiary">
                    <IonSelectOption value="nothing">Do not sync reading plan.</IonSelectOption>
                    <IonSelectOption value="backup">Backup this device's plan to the cloud.</IonSelectOption>
                    <IonSelectOption value="restore">Restore plan from the cloud to this device.</IonSelectOption>
                  </IonSelect>
                </IonItem>
                <IonText color="danger"
                  className={!showRequirement || !!readingPlanOptionSelected ? 'hidden' : ''}><br />** SELECT BIBLE READING PLAN SYNC OPTION **</IonText>
                <IonButton className="sync-data-button" color="tertiary" onClick={syncUserData}>
                  <IonIcon icon={sync} slot="start" />
                  <IonLabel>Synchronize Prayers and Plans</IonLabel> 
                </IonButton>
              </IonCol>
            </IonRow>
            :
            <IonRow >
              <IonCol className="announcement-text">
                <IonText color="tertiary">YOU MUST FIRST SIGN INTO A GROUP TO USE THIS SERVICE.</IonText>
                <IonButton className="sync-data-button" color="tertiary" routerLink={AppRouteInfo[AppRouteKeys.Fellowship].path}>
                  <IonIcon icon={person} slot="start" />
                  <IonLabel>Login or Register</IonLabel> 
                </IonButton>
              </IonCol>
            </IonRow>
          }
          <IonRow >
            <IonCol className="announcement-text">
              { !!syncReport.length &&
                <div className="sync-reported">
                  <h3>SYNC REPORT</h3>
                  { syncReport.map((message, index) => <p key={`report-${index}`} className="sync-report-record">{message}</p>) }
                </div>
              }
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonContent>
      <IonFooter>
        <div className="sync-ios-safe-zone"></div>
      </IonFooter>
      <NetworkNotification noInternetMessage="NO INTERNET CONNECTION DETECTED - Connection is required to sync data."></NetworkNotification>
    </IonPage>
  );
};

export default SyncUserData;